import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import {
  Credit,
  FinanceProvider,
  FinanceRate,
  Introduction,
  Job,
  JobCompanyInfo,
  Payment,
  WorkType,
} from '@kanda-libs/ks-frontend-services';

import { useRefreshingData } from 'hooks';
import { selectors } from 'store';
import {
  formatToPercentage,
  getCreditId,
  hasPaidByBank,
  hasPaidByCard,
} from 'utils';

export type Provider = FinanceProvider | 'example';

export interface StatusContextType {
  id: string | undefined;
  job: Job | undefined;
  jobCompanyInfo: JobCompanyInfo | undefined;
  credit: Credit | undefined;
  creditId: string | undefined;
  introduction: Introduction | undefined;
  rate: FinanceRate | undefined;
  rateTitle: string | undefined;
  deposit: Payment | undefined;
  provider: Provider | undefined;
  isLoading: boolean;
  isExampleJob: boolean;
  isDeclined: boolean;
  isIntroduced: boolean;
  isPaidByBank: boolean;
  isPaidByCard: boolean;
  isFinanced: boolean;
  isDeliveryOnly: boolean;
  workType: WorkType | undefined;
}

export const StatusContext = createContext<StatusContextType | null>(null);

export const useStatusContext = () =>
  useContext(StatusContext) as StatusContextType;

export interface StatusProviderProps {
  children: ReactNode;
}

const getDepositForLoan = (
  payments: Payment[],
  loanId: string,
): Payment | undefined => {
  const loanIndex = payments?.map((payment) => payment?.id)?.indexOf(loanId);
  if (!loanIndex) return undefined;
  const loan = payments[loanIndex];
  const nonLoanPayments = payments.filter(
    (payment) => payment?.payment_option?.payment_method !== 'loan',
  );
  if (nonLoanPayments && nonLoanPayments?.length === 1)
    return nonLoanPayments[0];
  const closest = nonLoanPayments.sort((p1: Payment, p2: Payment) => {
    const diff1 = Math.abs(
      new Date(p1?.metadata?.created_at || 0).getTime() -
        new Date(loan?.metadata?.created_at || 0).getTime(),
    );
    const diff2 = Math.abs(
      new Date(p2?.metadata?.created_at || 0).getTime() -
        new Date(loan?.metadata?.created_at || 0).getTime(),
    );
    return diff1 - diff2;
  });
  return closest[0];
};

const StatusProvider: FunctionComponent<StatusProviderProps> = function ({
  children,
}) {
  const { cleanUp } = useRefreshingData(15000);

  const { id } = useSelector(selectors.getPathKey);

  const job = useSelector(selectors.job.getEntity);
  const jobHasFetched = useSelector(selectors.job.getHasFetched);
  const jobCompanyInfo = useSelector(selectors.jobCompanyInfo.getEntity);
  const jobCompanyInfoHasFetched = useSelector(
    selectors.jobCompanyInfo.getHasFetched,
  );

  const credits = useSelector(selectors.credit.getEntities);
  const creditHasFetched = useSelector(selectors.credit.getHasFetched);
  const creditId = useMemo(() => getCreditId(job), [job]);
  const credit = useMemo(() => credits?.[creditId], [credits, creditId]);

  const introduction = useSelector(selectors.introduction.getEntity);
  const introductionHasFetched = useSelector(selectors.job.getHasFetched);

  const isPaidByBank = useMemo(() => hasPaidByBank(job), [job]);
  const isPaidByCard = useMemo(() => hasPaidByCard(job), [job]);
  const isFinanced = useMemo(() => {
    if (!job) return false;
    return job?.finance_status !== 'not_applied';
  }, [job]);
  const isDeclined = useMemo(() => job?.status === 'declined', [job]);

  const isExampleJob = useMemo(() => job?.flow_type === 'example', [job]);
  const isIntroduced = useMemo(() => Boolean(job?.introduced_to), [job]);

  const isLoading = useMemo(() => {
    if (!jobHasFetched || !jobCompanyInfoHasFetched || !job) return true;
    if (job?.introduced_to && (!introduction || !introductionHasFetched))
      return true;
    if (isPaidByBank || isPaidByCard) return false;
    return !creditHasFetched;
  }, [
    jobHasFetched,
    jobCompanyInfoHasFetched,
    job,
    creditHasFetched,
    introduction,
    introductionHasFetched,
    isPaidByBank,
    isPaidByCard,
  ]);

  const provider = useMemo((): Provider => {
    const creditProvider = credit?.provider;
    if (!creditProvider) return 'demo';
    return creditProvider;
  }, [credit]);

  const workType = useMemo(() => job?.work_type, [job]);

  const isDeliveryOnly = useMemo(() => {
    if (!workType) return false;
    return workType.includes('supply_only');
  }, [workType]);

  const deposit = useMemo((): Payment | undefined => {
    if (!job || !creditId) return undefined;
    if (job?.finance_status === 'not_applied') return undefined;
    const payments = job?.payments;
    if (!payments || payments?.length === 0) return undefined;
    const depositForLoan = getDepositForLoan(payments, creditId);
    if (!depositForLoan?.id) return undefined;
    return depositForLoan;
  }, [job, creditId]);

  const rate = useMemo(() => {
    if (!job) return undefined;
    return job?.customer_options?.finance_option;
  }, [job]);

  const rateTitle = useMemo(() => {
    if (!rate) return undefined;
    const {
      apr,
      apr_type: aprType,
      duration,
      deferred_duration: deferred,
    } = rate;
    if (apr === undefined || aprType === undefined || duration === undefined)
      return undefined;
    if (aprType === 'BUYNOW_PAYLATER') {
      if (!deferred) return 'Buy Now, Pay Later';
      return `Buy Now, Pay Later, ${deferred} months deferred, ${duration} repayments at ${formatToPercentage(
        apr,
      )} APR`;
    }
    if (aprType === 'INTEREST_FREE') {
      return `Interest Free, ${duration} repayments`;
    }
    return `Interest Bearing, ${duration} repayments at ${formatToPercentage(
      apr,
    )} APR`;
  }, [rate]);

  const values = useMemo(
    () => ({
      id,
      job,
      jobCompanyInfo,
      credit,
      creditId,
      introduction,
      rate,
      rateTitle,
      deposit,
      isLoading,
      isPaidByBank,
      isPaidByCard,
      isDeclined,
      isFinanced,
      isExampleJob,
      isIntroduced,
      isDeliveryOnly,
      provider,
      workType,
    }),
    [
      id,
      job,
      jobCompanyInfo,
      credit,
      creditId,
      introduction,
      rate,
      rateTitle,
      deposit,
      isLoading,
      isPaidByBank,
      isPaidByCard,
      isDeclined,
      isFinanced,
      isExampleJob,
      isIntroduced,
      isDeliveryOnly,
      provider,
      workType,
    ],
  );

  useEffect(() => () => cleanUp(), [cleanUp]);

  return (
    <StatusContext.Provider value={values}>{children}</StatusContext.Provider>
  );
};

export default StatusProvider;
